import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Building2, Users, AlertTriangle, CheckCircle2, Ban, Clock, LayoutGrid } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Vue d'ensemble"
        title="Tableau de bord"
        sub="Monitoring global des entreprises enregistrées sur PAG."
        right={
          <Link to="/admin/widgets" data-testid="admin-dashboard-widgets-link" className="inline-flex items-center h-10 px-4 bg-[#0A0A0A] hover:bg-[#002FA7] text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors">
            <LayoutGrid className="h-4 w-4 mr-2" /> Dashboard custom
          </Link>
        }
      />
      <div className="p-6 lg:p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock icon={Building2} label="Entreprises" value={stats?.total_entreprises ?? "—"} accent="#002FA7" testid="stat-total-entreprises" />
        <StatBlock icon={CheckCircle2} label="Actives" value={stats?.active ?? "—"} accent="#059669" testid="stat-active" />
        <StatBlock icon={Clock} label="En attente" value={stats?.pending_payment ?? "—"} accent="#FFD700" testid="stat-pending" />
        <StatBlock icon={Ban} label="Bloquées" value={stats?.blocked ?? "—"} accent="#E60000" testid="stat-blocked" />
      </div>
      <div className="px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatBlock icon={Users} label="Total salariés" value={stats?.total_salaries ?? "—"} accent="#0A0A0A" testid="stat-total-salaries" />
        <StatBlock icon={Building2} label="Total chefs d'entreprise" value={stats?.total_chefs ?? "—"} accent="#0A0A0A" testid="stat-total-chefs" />
      </div>
      <div className="p-6 lg:p-10">
        <div className="border border-[#DEE2E6] p-8 bg-[#F8F9FA]">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-5 w-5 text-[#002FA7] mt-1" />
            <div>
              <h3 className="font-display font-bold text-lg mb-2">Système de blocage automatique</h3>
              <p className="text-sm text-[#495057] max-w-3xl">
                Les entreprises en attente de paiement depuis plus de 48h sont automatiquement bloquées.
                Seul l'administrateur PAG peut débloquer l'accès après régularisation du paiement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, sub, right }) {
  return (
    <div className="border-b border-[#DEE2E6] bg-white">
      <div className="p-6 lg:p-10 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-2">// {eyebrow}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tighter" data-testid="page-title">{title}</h1>
          {sub && <p className="mt-2 text-sm text-[#495057] max-w-2xl">{sub}</p>}
        </div>
        {right}
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#DEE2E6] p-6 elev-hover">
      <div className="flex items-start justify-between mb-4">
        <div className="h-10 w-10 border border-[#DEE2E6] flex items-center justify-center">
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
      <div className="font-display text-4xl font-black tracking-tighter">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.25em] text-[#868E96] font-semibold">{label}</div>
    </div>
  );
}
